import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { tokenManager, type AuthUser } from '@/lib/tokenManager'
import { api } from '@/lib/api'
import { PLATFORM_TENANT_ID, type PermissionCode, type Role } from './permissions'
import { AuthContext, type AuthContextValue } from './AuthContext'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => tokenManager.getUser())

  useEffect(() => {
    // Bootstrap once: seed in-memory cache from localStorage and start
    // the silent-refresh timer.
    tokenManager.bootstrap()
    setUser(tokenManager.getUser())

    // React to any token mutation (login, logout, refresh, BroadcastChannel
    // message from another tab).
    const unsub = tokenManager.subscribe(() => setUser(tokenManager.getUser()))
    return unsub
  }, [])

  const hasPermission = useCallback(
    (code: PermissionCode | string) => {
      if (!user) return false
      // Platform-tenant super_admin (Bokapsys staff) bypass.
      if (user.role === 'super_admin' && user.tenantId === PLATFORM_TENANT_ID) return true
      return user.permissions.includes(code)
    },
    [user],
  )

  const hasRole = useCallback(
    (...roles: Role[]) => !!user && roles.includes(user.role as Role),
    [user],
  )

  const refresh = useCallback(() => tokenManager.refreshNow(), [])

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout')
    } catch {
      /* still clear local state below */
    }
    tokenManager.clear()
    setUser(null)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user,
      isPlatformAdmin:
        !!user && user.role === 'super_admin' && user.tenantId === PLATFORM_TENANT_ID,
      isImpersonating: !!user?.isImpersonating,
      hasPermission,
      hasRole,
      refresh,
      logout,
    }),
    [user, hasPermission, hasRole, refresh, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
